// src/components/reports/PostcodeMap.jsx
//
// Choropleth postcode density map for the Report Builder.
// Ported from takso-poc/takso-reporting-ui/src/pages/PostcodeMap.jsx.
//
// Supports two data sources (selectable by the user):
//   survey  — postcode answers from evaluation custom questions (POSTCODE type)
//   csv     — postcode column auto-detected in an uploaded CSV document
//
// Props:
//   projectId   {number}   Activity ID
//   orgId       {number}   Org ID (for API calls)

import { useEffect, useRef, useState, useCallback } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import axios from 'axios';
import html2canvas from 'html2canvas';

// ── Constants ─────────────────────────────────────────────────────────────────

const PHI = (1 + Math.sqrt(5)) / 2;
const FILL_COLOR = '#264DF7';
const BORDER_COLOR = '#1a3bbf';
const VIEW_RADIUS_KM = 200;

// ── Helpers ───────────────────────────────────────────────────────────────────

function boundsZoom(map, bounds, widthPx, heightPx) {
  const nw = bounds.getNorthWest();
  const se = bounds.getSouthEast();
  const nwPx = map.project(nw, 0);
  const sePx = map.project(se, 0);
  const bW = Math.abs(sePx.x - nwPx.x);
  const bH = Math.abs(sePx.y - nwPx.y);
  if (!bW || !bH) return 6;
  return Math.floor(Math.log2(Math.min(widthPx / bW, heightPx / bH)));
}

function haversineKm(lat1, lng1, lat2, lng2) {
  const R = 6371;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLng = ((lng2 - lng1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.asin(Math.sqrt(a));
}

function countToOpacity(count, maxCount) {
  if (!count || !maxCount) return 0;
  return 0.1 + 0.7 * Math.sqrt(count / maxCount);
}

function solidSwatchColor(count, maxCount) {
  const a = countToOpacity(count, maxCount);
  const r = Math.round(38 * a + 245 * (1 - a));
  const g = Math.round(77 * a + 245 * (1 - a));
  const b = Math.round(247 * a + 243 * (1 - a));
  return `rgb(${r},${g},${b})`;
}

function featureStyle(count, maxCount) {
  return {
    fillColor: count > 0 ? solidSwatchColor(count, maxCount) : '#e5e7eb',
    fillOpacity: count > 0 ? 1.0 : 0.15,
    color: '#ffffff',
    weight: 1,
    opacity: 1,
  };
}

// ── Canvas heatmap (Leaflet 1.x, no plugin dependency) ────────────────────────
//
// Three-pass polygon-shaped heatmap:
//   Pass 1 — rasterise each postcode polygon filled at sqrt(count/max) intensity
//   Pass 2 — Gaussian-blur the rasterised layer so polygon edges blend softly
//   Pass 3 — colorise each pixel via a thermal gradient palette lookup
//
// Args: geoLayer — Leaflet GeoJSON layer (may be removed from map; geometry intact)
//       countMap — Map<postcode, count>
//
const CanvasHeat = L.Layer.extend({
  initialize(geoLayer, countMap, options) {
    this._geoLayer = geoLayer;
    this._countMap = countMap;
    L.setOptions(this, options);
  },

  onAdd(map) {
    this._map = map;
    if (!this._canvas) {
      this._canvas = document.createElement('canvas');
      this._canvas.className = 'leaflet-layer';
      // max-width:none overrides the global "canvas { max-width: 100% }" rule in
      // index.scss — without it the shadow pane collapses the canvas width to 0.
      this._canvas.style.cssText =
        'position:absolute;top:0;left:0;pointer-events:none;max-width:none;opacity:0.96;';
    }
    map.getPane('shadowPane').appendChild(this._canvas);
    map.on('moveend zoomend viewreset resize', this._draw, this);
    this._draw();
  },

  onRemove(map) {
    if (this._canvas?.parentNode)
      this._canvas.parentNode.removeChild(this._canvas);
    map.off('moveend zoomend viewreset resize', this._draw, this);
  },

  _buildPalette() {
    const c = document.createElement('canvas');
    c.width = 256;
    c.height = 1;
    const ctx = c.getContext('2d');
    const g = ctx.createLinearGradient(0, 0, 256, 0);
    const stops = this.options.gradient || {
      // Lighter cool end + gamma correction on lookup (see Pass 3) removes
      // the dark ring.  Palette itself stays perceptually smooth; the gamma
      // curve does the heavy lifting of pushing warm colours into the
      // transition zone.
      0.0: 'rgba(100,140,255,0)', // transparent light-blue
      0.2: '#6699ff', // cornflower — lighter than old #3d5eff
      0.38: '#00bbff', // sky blue
      0.54: '#00ffdd', // aquamarine
      0.67: '#aaff00', // chartreuse
      0.8: '#ffcc00', // golden amber
      0.91: '#ff6600', // orange
      1.0: '#ff1100', // red
    };
    Object.entries(stops).forEach(([p, col]) => g.addColorStop(+p, col));
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, 256, 1);
    return ctx.getImageData(0, 0, 256, 1).data;
  },

  // Flatten Leaflet getLatLngs() nesting into an array of outer rings.
  // Polygon → [[LatLng,…]]   MultiPolygon → [[LatLng,…], [LatLng,…], …]
  _rings(latlngs) {
    if (!latlngs?.length) return [];
    if (latlngs[0] instanceof L.LatLng) return [latlngs]; // bare ring
    if (latlngs[0][0] instanceof L.LatLng) return [latlngs[0]]; // Polygon — outer ring
    return latlngs.map((poly) => poly[0]); // MultiPolygon
  },

  _draw() {
    const map = this._map;
    const size = map.getSize();
    const cv = this._canvas;

    // Align canvas with the viewport (mapPane CSS translate compensation).
    L.DomUtil.setPosition(cv, map.containerPointToLayerPoint([0, 0]));
    cv.width = size.x;
    cv.height = size.y;
    if (!this._geoLayer) return;

    const max = this.options.max || 1;
    const baseBlur = this.options.blur || 20;
    // Scale blur proportionally to zoom so postcodes at lower zoom levels
    // retain the same apparent sharpness as they have at zoom 10.
    // Each zoom step halves/doubles the pixel size of a postcode, so we
    // halve the blur for each step below 10 (clamped to a 2px minimum).
    const zoom = map.getZoom();
    const blur = Math.max(2, Math.round(baseBlur * Math.pow(2, zoom - 10)));

    // ── Pass 1: rasterise postcode polygons at their intensity ────────────────
    const raw = document.createElement('canvas');
    raw.width = size.x;
    raw.height = size.y;
    const rctx = raw.getContext('2d');

    this._geoLayer.eachLayer((sub) => {
      const fp = sub.feature?.properties || {};
      const code = fp.poa_code_2021 || fp.POA_CODE_2021;
      const count = this._countMap.get(code) || 0;
      if (!count) return;

      // Floor at 0.15 so every active postcode is visible; scale the remaining
      // 0.85 linearly so the densest postcode reaches full opacity and contrast
      // is clear even at zoomed-out view.
      rctx.globalAlpha = 0.35 + 0.65 * (count / max);
      rctx.fillStyle = '#ffffff';

      this._rings(sub.getLatLngs()).forEach((ring) => {
        rctx.beginPath();
        ring.forEach((ll, i) => {
          const pt = map.latLngToContainerPoint(ll);
          if (i === 0) rctx.moveTo(pt.x, pt.y);
          else rctx.lineTo(pt.x, pt.y);
        });
        rctx.closePath();
        rctx.fill();
      });
    });

    // ── Pass 2: blur so polygon edges blend softly into each other ────────────
    const blurred = document.createElement('canvas');
    blurred.width = size.x;
    blurred.height = size.y;
    const bctx = blurred.getContext('2d');
    bctx.filter = `blur(${blur}px)`;
    bctx.drawImage(raw, 0, 0);

    // ── Pass 3: colorise via thermal gradient palette ─────────────────────────
    const img = bctx.getImageData(0, 0, size.x, size.y);
    const px = img.data;
    const pal = this._buildPalette();

    for (let i = 0; i < px.length; i += 4) {
      const a = px[i + 3];
      if (!a) continue;
      // Gamma < 1 pushes the palette lookup toward warmer colours for
      // mid-range pixels, shrinking the dark-blue transition ring without
      // changing overall opacity (we preserve the original alpha).
      const idx = Math.min(255, Math.round(Math.pow(a / 255, 0.75) * 255)) * 4;
      px[i] = pal[idx];
      px[i + 1] = pal[idx + 1];
      px[i + 2] = pal[idx + 2];
      // Keep original alpha — gamma only shifts the colour, not transparency.
    }
    cv.getContext('2d').putImageData(img, 0, 0);
  },
});

async function fetchBoundaries(t, postcodes) {
  const { data } = await axios.get('/report-builder/postcode-boundaries', {
    params: { postcodes: postcodes.join(',') },
  });
  if (!data.ok)
    throw new Error(data.error || t('postcode_maps.errors.boundaries_error'));
  return { type: 'FeatureCollection', features: data.features || [] };
}

// ── Component ─────────────────────────────────────────────────────────────────

const PostcodeMap = ({
  projectId,
  showReportToggle = false,
  onReportToggle = null,
}) => {
  const { t } = useTranslation();

  // ── Data source state ──────────────────────────────────────────────────────
  const [availableSources, setAvailableSources] = useState(null); // null = loading
  const [activeSource, setActiveSource] = useState('survey');
  const [csvDocuments, setCsvDocuments] = useState([]);
  const [selectedDoc, setSelectedDoc] = useState(null);

  // ── CSV column resolution ──────────────────────────────────────────────────
  // When the backend cannot auto-detect a postcode column it returns a 422
  // with column metadata. csvPick holds that metadata; pickedColumn is the
  // key the user confirms or selects.
  const [csvPick, setCsvPick] = useState(null); // null | { fileName, columns, hasHeader, filenameHint, rowCount }
  const [pickedColumn, setPickedColumn] = useState(null); // null | column key string
  const [pendingColumn, setPendingColumn] = useState(null); // selection in progress inside picker

  // ── Map data state ─────────────────────────────────────────────────────────
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [geoStatus, setGeoStatus] = useState('idle');
  const [selectedCode, setSelectedCode] = useState(null); // postcode string | null
  const [arcgisNames, setArcgisNames] = useState({});
  const [outRangeCodes, setOutRangeCodes] = useState(new Set());
  const [showLegend, setShowLegend] = useState(true);

  // ── Map options ────────────────────────────────────────────────────────────
  const [showLabels, setShowLabels] = useState(false);
  const [mapType, setMapType] = useState('boundary'); // 'boundary' | 'heatmap'
  const [zoomPreset, setZoomPreset] = useState('auto'); // 'auto' | numeric string
  const labelsLayerRef = useRef(null);
  const heatLayerRef = useRef(null);
  const heatPointsRef = useRef([]); // [[lat, lng, count], …] — populated when GeoJSON loads
  const mapTypeRef = useRef('boundary'); // mirrors mapType so GeoJSON closure sees current value

  // ── Report controls state ──────────────────────────────────────────────────
  const [viewMode, setViewMode] = useState('interactive'); // 'interactive' | 'report'
  const [included, setIncluded] = useState(false);
  const [savingPng, setSavingPng] = useState(false);
  const reportAreaRef = useRef(null);

  const capturePng = useCallback(
    () =>
      new Promise((resolve, reject) => {
        const el = reportAreaRef.current;
        if (!el) return reject(new Error('No element'));
        html2canvas(el, { backgroundColor: '#ffffff', scale: 2, useCORS: true })
          .then((canvas) => resolve(canvas.toDataURL('image/png')))
          .catch(reject);
      }),
    []
  );

  const handleSavePng = useCallback(async () => {
    setSavingPng(true);
    try {
      const dataUrl = await capturePng();
      const link = document.createElement('a');
      link.download = 'postcode-map.png';
      link.href = dataUrl;
      link.click();
    } finally {
      setSavingPng(false);
    }
  }, [capturePng]);

  const handleReportToggle = useCallback(
    async (value) => {
      setIncluded(value);
      if (value) {
        // Switch to report view and capture PNG after tiles settle
        setViewMode('report');
        setTimeout(async () => {
          try {
            const dataUrl = await capturePng();
            if (onReportToggle) onReportToggle(true, dataUrl);
          } catch (_) {
            if (onReportToggle) onReportToggle(true, null);
          }
        }, 600);
      } else {
        if (onReportToggle) onReportToggle(false, null);
      }
    },
    [capturePng, onReportToggle]
  );

  // ── Map sizing ─────────────────────────────────────────────────────────────
  const mapContainerRef = useRef(null);
  const [mapW, setMapW] = useState(680);
  const mapWRef = useRef(680);
  const zoomPresetRef = useRef('auto');
  const mapH = Math.round(mapW / PHI);

  // ── Leaflet refs ───────────────────────────────────────────────────────────
  const mapDivRef = useRef(null);
  const leafletRef = useRef(null);
  const geoLayerRef = useRef(null);
  const boundsRef = useRef(null);
  const postcodeCountRef = useRef(new Map());
  const resizeTimerRef = useRef(null);

  // Keep refs in sync
  useEffect(() => {
    mapWRef.current = mapW;
  }, [mapW]);
  useEffect(() => {
    mapTypeRef.current = mapType;
  }, [mapType]);
  useEffect(() => {
    zoomPresetRef.current = zoomPreset;
  }, [zoomPreset]);

  // Load saved zoom preset for this project on mount
  useEffect(() => {
    if (!projectId) return;
    const saved = localStorage.getItem(`pm-zoom-${projectId}`);
    if (saved) setZoomPreset(saved);
  }, [projectId]);

  // Apply zoom preset when the user changes it interactively.
  // Intentionally does NOT depend on geoStatus — initial-load zoom is handled
  // inside the requestAnimationFrame in the GeoJSON effect, avoiding a race
  // between two simultaneous animated setView calls.
  useEffect(() => {
    const map = leafletRef.current;
    if (!map || !boundsRef.current) return; // data not loaded yet — rAF will handle it
    try {
      if (zoomPreset === 'auto') {
        const w = mapWRef.current;
        const z = boundsZoom(map, boundsRef.current, w, Math.round(w / PHI));
        map.setView(boundsRef.current.getCenter(), z, { animate: false });
      } else {
        map.setZoom(Number(zoomPreset), { animate: false });
      }
    } catch (_) {}
  }, [zoomPreset]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Measure container width ────────────────────────────────────────────────
  useEffect(() => {
    const el = mapContainerRef.current;
    if (!el) return;
    const measure = () => {
      const w = el.getBoundingClientRect().width;
      if (w > 0) {
        const next = Math.min(860, Math.max(300, w));
        setMapW(next);
        mapWRef.current = next;
      }
    };
    const obs = new ResizeObserver(measure);
    obs.observe(el);
    measure();
    return () => obs.disconnect();
  }, []);

  // ── Refit Leaflet on resize ────────────────────────────────────────────────
  useEffect(() => {
    const map = leafletRef.current;
    if (!map) return;
    clearTimeout(resizeTimerRef.current);
    resizeTimerRef.current = setTimeout(() => {
      map.invalidateSize();
      if (boundsRef.current) {
        const z = boundsZoom(map, boundsRef.current, mapW, mapH);
        map.setView(boundsRef.current.getCenter(), z, { animate: false });
      }
    }, 150);
    return () => clearTimeout(resizeTimerRef.current);
  }, [mapW, mapH]);

  // ── Check available sources on mount ──────────────────────────────────────
  useEffect(() => {
    if (!projectId) return;
    axios
      .get(`/project/${projectId}/report-builder/csv-documents`)
      .then(({ data: d }) => {
        setCsvDocuments(d.documents || []);
        setAvailableSources({
          survey: true,
          csv: (d.documents || []).length > 0,
        });
        if (d.documents?.length > 0) setSelectedDoc(d.documents[0].documentId);
      })
      .catch(() => setAvailableSources({ survey: true, csv: false }));
  }, [projectId]);

  // ── Fetch postcode data when source/doc/pickedColumn changes ──────────────
  const fetchData = useCallback(() => {
    if (!projectId) return;
    if (activeSource === 'csv' && !selectedDoc) return;
    // For CSV: if the picker is showing (csvPick set, no column confirmed yet),
    // hold off — the confirm/select buttons will set pickedColumn and re-trigger.
    if (activeSource === 'csv' && csvPick && !pickedColumn) return;

    setLoading(true);
    setError(null);
    setData(null);
    setGeoStatus('idle');

    const params = { source: activeSource };
    if (activeSource === 'csv') {
      params.documentId = selectedDoc;
      if (pickedColumn) params.column = pickedColumn;
    }

    axios
      .get(`/project/${projectId}/report-builder/postcode-map`, { params })
      .then(({ data: d }) => {
        if (!d.ok) throw new Error(d.error || 'API error');
        setData(d);
      })
      .catch((e) => {
        const resp = e.response?.data;
        if (resp?.error === 'no_column_detected') {
          // Backend couldn't detect a postcode column — show the picker
          setCsvPick(resp);
          setPendingColumn(resp.columns?.[0]?.key || null);
        } else if (e.response) {
          // Real HTTP error — show the server-side message
          setError(resp?.error || e.message);
        } else if (e.message) {
          // Non-HTTP error (e.g. network, interceptor artifact) — log only
          console.error('[PostcodeMap] fetch error:', e.message);
        }
      })
      .finally(() => setLoading(false));
  }, [projectId, activeSource, selectedDoc, csvPick, pickedColumn]); // eslint-disable-line react-hooks/exhaustive-deps

  // Reset column pick state when the user switches source or document
  useEffect(() => {
    setCsvPick(null);
    setPickedColumn(null);
    setPendingColumn(null);
  }, [activeSource, selectedDoc]);

  useEffect(() => {
    if (availableSources) fetchData();
  }, [fetchData, availableSources]);

  // ── Initialise Leaflet ─────────────────────────────────────────────────────
  useEffect(() => {
    if (!mapDivRef.current || leafletRef.current) return;
    const map = L.map(mapDivRef.current, {
      center: [-36.5, 144.5],
      zoom: 6,
      zoomControl: false,
    });
    L.tileLayer(
      `https://{s}.basemaps.cartocdn.com/light_nolabels/{z}/{x}/{y}{r}.png?key=${
        import.meta.env.REACT_APP_CARTO_API_KEY
      }`,
      {
        attribution:
          '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> &copy; <a href="https://carto.com/attributions">CARTO</a>',
        subdomains: 'abcd',
        maxZoom: 19,
      }
    ).addTo(map);
    map.on('click', () => setSelectedCode(null));
    leafletRef.current = map;
    return () => {
      map.remove();
      leafletRef.current = null;
    };
  }, []);

  // ── Place labels overlay ───────────────────────────────────────────────────
  useEffect(() => {
    const map = leafletRef.current;
    if (!map) return;
    if (showLabels) {
      if (!labelsLayerRef.current) {
        labelsLayerRef.current = L.tileLayer(
          `https://{s}.basemaps.cartocdn.com/light_only_labels/{z}/{x}/{y}{r}.png?key=${
            import.meta.env.REACT_APP_CARTO_API_KEY
          }`,
          { subdomains: 'abcd', maxZoom: 19, pane: 'shadowPane' }
        );
      }
      labelsLayerRef.current.addTo(map);
    } else {
      labelsLayerRef.current?.remove();
    }
  }, [showLabels]);

  // ── Load GeoJSON when data arrives ────────────────────────────────────────
  useEffect(() => {
    const map = leafletRef.current;
    if (!map || !data) return;

    // Remove old layer
    if (geoLayerRef.current) {
      geoLayerRef.current.remove();
      geoLayerRef.current = null;
    }

    const pcMap = new Map(data.postcodes.map((p) => [p.postcode, p.count]));
    const maxCount = Math.max(...data.postcodes.map((p) => p.count), 1);
    postcodeCountRef.current = pcMap;

    setGeoStatus('loading');
    fetchBoundaries(
      t,
      data.postcodes.map((p) => p.postcode)
    )
      .then((geojson) => {
        setGeoStatus('ok');
        const names = {};
        const layer = L.geoJSON(geojson, {
          style: (f) => {
            const p = f.properties || {};
            const code = p.poa_code_2021 || p.POA_CODE_2021;
            return featureStyle(pcMap.get(code) || 0, maxCount);
          },
          onEachFeature: (f, sub) => {
            const p = f.properties || {};
            const code = p.poa_code_2021 || p.POA_CODE_2021;
            const name = p.poa_name_2021 || p.POA_NAME_2021;
            if (code && name && name !== code) names[code] = name;
            sub.on('mouseover', function () {
              if (mapTypeRef.current === 'heatmap') return; // heatmap needs no hover highlight
              this.setStyle({ color: BORDER_COLOR, weight: 2, opacity: 1 });
              this.bringToFront();
            });
            sub.on('mouseout', function () {
              if (mapTypeRef.current === 'heatmap') {
                this.setStyle({
                  fillOpacity: 0,
                  color: '#94a3b8',
                  weight: 1,
                  opacity: 0.5,
                });
                return;
              }
              const mc2 = Math.max(
                ...Array.from(postcodeCountRef.current.values()),
                1
              );
              this.setStyle(
                featureStyle(postcodeCountRef.current.get(code) || 0, mc2)
              );
            });
            sub.on('click', function (e) {
              L.DomEvent.stopPropagation(e);
              setSelectedCode((prev) => (prev === code ? null : code));
              try {
                const bounds = this.getBounds();
                if (bounds.isValid()) {
                  map.fitBounds(bounds.pad(0.6), {
                    animate: true,
                    maxZoom: 13,
                    duration: 0.5,
                  });
                }
              } catch (_) {}
            });
          },
        }).addTo(map);

        geoLayerRef.current = layer;
        setArcgisNames(names);

        if (!geojson.features?.length) return;

        const topCode = [...pcMap.entries()].sort(
          (a, b) => b[1] - a[1]
        )[0]?.[0];
        let topCenter = null;
        layer.eachLayer((sub) => {
          const fp = sub.feature?.properties || {};
          if ((fp.poa_code_2021 || fp.POA_CODE_2021) === topCode) {
            try {
              topCenter = sub.getBounds().getCenter();
            } catch (_) {}
          }
        });

        const inRange = new Set();
        const outRange = new Set();
        layer.eachLayer((sub) => {
          const fp = sub.feature?.properties || {};
          const code = fp.poa_code_2021 || fp.POA_CODE_2021;
          if (!code) return;
          try {
            const c = sub.getBounds().getCenter();
            const dist = topCenter
              ? haversineKm(topCenter.lat, topCenter.lng, c.lat, c.lng)
              : 0;
            (dist <= VIEW_RADIUS_KM ? inRange : outRange).add(code);
          } catch (_) {
            inRange.add(code);
          }
        });
        data.postcodes.forEach((p) => {
          if (!inRange.has(p.postcode) && !outRange.has(p.postcode))
            outRange.add(p.postcode);
        });
        setOutRangeCodes(outRange);

        // Build heatmap points — centroid of each in-range postcode, weighted by count
        const pts = [];
        layer.eachLayer((sub) => {
          const fp = sub.feature?.properties || {};
          const code = fp.poa_code_2021 || fp.POA_CODE_2021;
          if (!code || !inRange.has(code)) return;
          try {
            const c = sub.getBounds().getCenter();
            pts.push([c.lat, c.lng, pcMap.get(code) || 0]);
          } catch (_) {}
        });
        heatPointsRef.current = pts;

        // If the user switched to heatmap before GeoJSON finished loading,
        // build it now that geometry is available.  Restyle the boundary layer
        // to outline-only so it acts as a reference grid beneath the heatmap.
        if (mapTypeRef.current === 'heatmap') {
          layer.setStyle({
            fillOpacity: 0,
            color: '#94a3b8',
            weight: 1,
            opacity: 0.5,
          });
          try {
            if (heatLayerRef.current) heatLayerRef.current.remove();
          } catch (_) {}
          const maxVal = Math.max(...Array.from(pcMap.values()), 1);
          try {
            heatLayerRef.current = new CanvasHeat(layer, pcMap, {
              blur: 20,
              max: maxVal,
            }).addTo(map);
          } catch (_) {}
        }

        let focusBounds = null;
        layer.eachLayer((sub) => {
          const fp = sub.feature?.properties || {};
          const code = fp.poa_code_2021 || fp.POA_CODE_2021;
          if (!inRange.has(code)) return;
          try {
            const b = sub.getBounds();
            if (b?.isValid())
              focusBounds = focusBounds
                ? focusBounds.extend(b)
                : L.latLngBounds(b.getSouthWest(), b.getNorthEast());
          } catch (_) {}
        });

        const viewBounds = (focusBounds || layer.getBounds()).pad(0.1);
        boundsRef.current = viewBounds;
        requestAnimationFrame(() => {
          map.invalidateSize();
          const w = mapWRef.current;
          const preset = zoomPresetRef.current;
          const z =
            preset === 'auto'
              ? boundsZoom(map, viewBounds, w, Math.round(w / PHI))
              : Number(preset);
          map.setView(viewBounds.getCenter(), z, { animate: false });
        });
      })
      .catch(() => setGeoStatus('unavailable'));
  }, [data]);

  // ── Switch between boundary choropleth and heatmap ───────────────────────
  // In heatmap mode the GeoJSON layer stays on the map but is restyled to
  // outline-only (no fill) so postcode shapes are visible as a reference grid
  // beneath the thermal canvas.  The CanvasHeat lives in the shadowPane
  // (z-500) above the GeoJSON overlayPane (z-400), so it renders on top.
  useEffect(() => {
    const map = leafletRef.current;
    if (!map) return;

    const restoreChoropleth = () => {
      const layer = geoLayerRef.current;
      if (!layer) return;
      const mc = Math.max(...Array.from(postcodeCountRef.current.values()), 1);
      layer.eachLayer((sub) => {
        const fp = sub.feature?.properties || {};
        const code = fp.poa_code_2021 || fp.POA_CODE_2021;
        sub.setStyle(featureStyle(postcodeCountRef.current.get(code) || 0, mc));
      });
    };

    const applyOutlineOnly = () => {
      const layer = geoLayerRef.current;
      if (!layer) return;
      layer.setStyle({
        fillOpacity: 0,
        color: '#94a3b8',
        weight: 1,
        opacity: 0.5,
      });
    };

    if (mapType === 'heatmap') {
      applyOutlineOnly();

      // Remove stale heatmap
      try {
        if (heatLayerRef.current) heatLayerRef.current.remove();
      } catch (_) {}
      heatLayerRef.current = null;

      if (geoLayerRef.current && postcodeCountRef.current.size > 0) {
        const maxVal = Math.max(
          ...Array.from(postcodeCountRef.current.values()),
          1
        );
        try {
          heatLayerRef.current = new CanvasHeat(
            geoLayerRef.current,
            postcodeCountRef.current,
            {
              blur: 20,
              max: maxVal,
            }
          ).addTo(map);
        } catch (e) {
          console.error('[PostcodeMap] CanvasHeat error:', e);
        }
      }
    } else {
      // Remove heatmap canvas and restore choropleth fills
      try {
        if (heatLayerRef.current) heatLayerRef.current.remove();
      } catch (_) {}
      heatLayerRef.current = null;
      restoreChoropleth();
    }
  }, [mapType]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Legend row click — zoom to postcode on map + show detail panel ───────
  const handleListClick = useCallback((e, code) => {
    e.preventDefault();
    setSelectedCode((prev) => (prev === code ? null : code));
    // Find the matching layer and zoom to its bounds
    const layer = geoLayerRef.current;
    if (!layer) return;
    layer.eachLayer((sub) => {
      const fp = sub.feature?.properties || {};
      if ((fp.poa_code_2021 || fp.POA_CODE_2021) !== code) return;
      try {
        const bounds = sub.getBounds();
        if (bounds.isValid()) {
          leafletRef.current?.fitBounds(bounds.pad(0.6), {
            animate: true,
            maxZoom: 13,
            duration: 0.5,
          });
        }
      } catch (_) {}
    });
  }, []);

  const maxCount = data
    ? Math.max(...data.postcodes.map((p) => p.count), 1)
    : 1;
  const responsesWithPostcode =
    data?.postcodes?.reduce((s, p) => s + p.count, 0) || 0;

  // Detail panel data for selected postcode
  const selectedPostcode = selectedCode
    ? data?.postcodes?.find((p) => p.postcode === selectedCode)
    : null;
  const selectedLocality = selectedCode
    ? data?.localities?.[selectedCode] || arcgisNames[selectedCode] || null
    : null;
  const selectedPct =
    selectedPostcode && responsesWithPostcode > 0
      ? Math.round((selectedPostcode.count / responsesWithPostcode) * 100)
      : 0;

  // ── Render ─────────────────────────────────────────────────────────────────
  if (!projectId)
    return <div className="pm-empty">{t('activity.no_activity_selected')}</div>;

  const isReport = viewMode === 'report';

  return (
    <div className="pm-wrap">
      {/* ── View tab bar + Include in report ── */}
      {showReportToggle && (
        <div className="pm-toolbar">
          {/* Tab switcher */}
          <div className="pm-tabs">
            <button
              type="button"
              className={`pm-tabs__tab${
                !isReport ? ' pm-tabs__tab--active' : ''
              }`}
              onClick={() => setViewMode('interactive')}
            >
              {t('maps.interactive')}
            </button>
            <button
              type="button"
              className={`pm-tabs__tab${
                isReport ? ' pm-tabs__tab--active' : ''
              }`}
              onClick={() => setViewMode('report')}
            >
              {t('maps.report_view')}
            </button>
          </div>

          {/* Save PNG — only in report view */}
          {isReport && (
            <button
              className="ch__save-png"
              onClick={handleSavePng}
              disabled={savingPng}
            >
              {savingPng
                ? `${t('common.saving')}…`
                : `⬇ ${t('common.save_png')}`}
            </button>
          )}

          {/* Include in report Yes/No */}
          <div className="ch__include">
            <span className="ch__include-label">
              {t('report.include_in_report')}
            </span>
            <div className="ch__include-btns">
              <button
                className={`ch__include-btn ch__include-btn--yes ${
                  included
                    ? 'ch__include-btn--active'
                    : 'ch__include-btn--inactive'
                }`}
                onClick={() => {
                  if (!included) handleReportToggle(true);
                }}
              >
                {t('actions.yes')}
              </button>
              <button
                className={`ch__include-btn ${
                  !included
                    ? 'ch__include-btn--active'
                    : 'ch__include-btn--inactive'
                }`}
                onClick={() => {
                  if (included) handleReportToggle(false);
                }}
              >
                {t('actions.no')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Data source selector — interactive only ── */}
      {!isReport && availableSources && (
        <div className="pm-source-bar">
          <span className="pm-source-bar__label">
            {t('common.data_source')}:
          </span>

          <button
            type="button"
            className={`pm-source-bar__btn${
              activeSource === 'survey' ? ' pm-source-bar__btn--active' : ''
            }`}
            onClick={() => setActiveSource('survey')}
          >
            {t('report.survey_responses')}
          </button>

          {availableSources.csv ? (
            <>
              <button
                type="button"
                className={`pm-source-bar__btn${
                  activeSource === 'csv' ? ' pm-source-bar__btn--active' : ''
                }`}
                onClick={() => setActiveSource('csv')}
              >
                {t('csv.import.external_csv')}
              </button>
              {activeSource === 'csv' && csvDocuments.length > 1 && (
                <select
                  className="pm-source-bar__select sml"
                  value={selectedDoc || ''}
                  onChange={(e) => setSelectedDoc(Number(e.target.value))}
                >
                  {csvDocuments.map((d) => (
                    <option key={d.documentId} value={d.documentId}>
                      {d.name}
                    </option>
                  ))}
                </select>
              )}
              {activeSource === 'csv' && csvDocuments.length === 1 && (
                <span className="pm-source-bar__doc-name">
                  {csvDocuments[0].name}
                </span>
              )}
            </>
          ) : (
            <span
              className="pm-source-bar__btn pm-source-bar__btn--disabled"
              title={t('postcode_maps.csv.upload_to_enable')}
            >
              <Trans
                i18nKey="postcode_maps.csv.external_csv_no_csv_uploaded"
                components={{ span: <span className="pm-source-bar__hint" /> }}
              />
            </span>
          )}
        </div>
      )}

      {/* ── CSV column picker — shown when backend cannot auto-detect ── */}
      {!isReport &&
        csvPick &&
        !pickedColumn &&
        (() => {
          const { fileName, columns, filenameHint, rowCount } = csvPick;
          const isSingleCol = columns.length === 1;
          const isConfirm = isSingleCol && filenameHint;

          if (isConfirm) {
            // Simple confirm: 1 column + filename contains a postcode keyword
            return (
              <div className="pm-col-pick pm-col-pick--confirm">
                <span className="pm-col-pick__icon">📍</span>
                <span className="pm-col-pick__msg">
                  <Trans
                    i18nKey="postcode_maps.csv.no_column_heading"
                    values={{
                      fileName,
                      postcodes: t('postcode_maps.num_postcodes', {
                        count: rowCount,
                      }),
                    }}
                  />
                </span>
                <div className="pm-col-pick__actions">
                  <button
                    type="button"
                    className="pm-col-pick__btn pm-col-pick__btn--primary"
                    onClick={() => {
                      setCsvPick(null);
                      setPickedColumn(columns[0].key);
                    }}
                  >
                    {t('maps.yes_load_map')}
                  </button>
                  <button
                    type="button"
                    className="pm-col-pick__btn"
                    onClick={() => {
                      setCsvPick(null);
                      setActiveSource('survey');
                    }}
                  >
                    {t('actions.cancel')}
                  </button>
                </div>
              </div>
            );
          }

          // Column picker: show all columns with sample values
          return (
            <div className="pm-col-pick">
              <p className="pm-col-pick__msg">
                <Trans
                  i18nKey="postcode_maps.csv.select_poctcode_column"
                  values={{
                    fileName,
                    columns: t('csv.num_columns', { count: columns.length }),
                  }}
                />
              </p>
              <table className="pm-col-pick__table">
                <tbody>
                  {columns.map((col) => (
                    <tr
                      key={col.key}
                      className={`pm-col-pick__row${
                        pendingColumn === col.key
                          ? ' pm-col-pick__row--selected'
                          : ''
                      }`}
                      onClick={() => setPendingColumn(col.key)}
                    >
                      <td className="pm-col-pick__radio">
                        <input
                          type="radio"
                          name="csvColumn"
                          checked={pendingColumn === col.key}
                          onChange={() => setPendingColumn(col.key)}
                        />
                      </td>
                      <td className="pm-col-pick__colname">
                        {col.displayName}
                      </td>
                      <td className="pm-col-pick__samples">
                        {col.samples.join(' · ')}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="pm-col-pick__actions">
                <button
                  type="button"
                  className="pm-col-pick__btn pm-col-pick__btn--primary"
                  disabled={!pendingColumn}
                  onClick={() => {
                    setCsvPick(null);
                    setPickedColumn(pendingColumn);
                  }}
                >
                  {t('csv.import.use_this_column')}
                </button>
                <button
                  type="button"
                  className="pm-col-pick__btn"
                  onClick={() => {
                    setCsvPick(null);
                    setActiveSource('survey');
                  }}
                >
                  {t('actions.cancel')}
                </button>
              </div>
            </div>
          );
        })()}

      {/* ── Map options — interactive only ── */}
      {!isReport && (
        <div className="pm-options">
          <span className="pm-options__heading">{t('maps.map_options')}</span>
          <div className="pm-options__row">
            <span className="pm-options__label">{t('maps.map_type')}</span>
            <div className="ch__include-btns">
              <button
                type="button"
                className={`ch__include-btn ch__include-btn--yes ${
                  mapType === 'boundary'
                    ? 'ch__include-btn--active'
                    : 'ch__include-btn--inactive'
                }`}
                onClick={() => setMapType('boundary')}
              >
                {t('maps.boundary')}
              </button>
              <button
                type="button"
                className={`ch__include-btn ${
                  mapType === 'heatmap'
                    ? 'ch__include-btn--active'
                    : 'ch__include-btn--inactive'
                }`}
                onClick={() => setMapType('heatmap')}
              >
                {t('maps.heatmap')}
              </button>
            </div>
          </div>

          <div className="pm-options__row">
            <span className="pm-options__label">
              {t('postcode_maps.place_labels')}
            </span>
            <div className="ch__include-btns">
              <button
                type="button"
                className={`ch__include-btn ch__include-btn--yes ${
                  showLabels
                    ? 'ch__include-btn--active'
                    : 'ch__include-btn--inactive'
                }`}
                onClick={() => setShowLabels(true)}
              >
                {t('actions.yes')}
              </button>
              <button
                type="button"
                className={`ch__include-btn ${
                  !showLabels
                    ? 'ch__include-btn--active'
                    : 'ch__include-btn--inactive'
                }`}
                onClick={() => setShowLabels(false)}
              >
                {t('actions.no')}
              </button>
            </div>
          </div>

          <div className="pm-options__row">
            <span className="pm-options__label">{t('maps.zoom_level')}</span>
            <select
              className="pm-options__zoom-select sml"
              value={zoomPreset}
              onChange={(e) => {
                const v = e.target.value;
                setZoomPreset(v);
                if (projectId) localStorage.setItem(`pm-zoom-${projectId}`, v);
              }}
            >
              <option value="auto">{t('postcode_maps.fit_to_data')}</option>
              <option value="5">5</option>
              <option value="6">6</option>
              <option value="7">7</option>
              <option value="8">8</option>
              <option value="9">9</option>
              <option value="10">10</option>
              <option value="11">11</option>
              <option value="12">12</option>
            </select>
          </div>
        </div>
      )}

      {loading && (
        <div className="pm-loading">
          {t('postcode_maps.loading_postcode_data')}…
        </div>
      )}
      {error && (
        <div className="pm-error">
          {t('common.error')}: {error}
        </div>
      )}
      {!loading && !error && data && data.postcodes.length === 0 && (
        <div className="pm-empty">
          {activeSource === 'survey'
            ? t('postcode_maps.no_postcode_responses')
            : t('postcode_maps.no_valid_postcodes_in_csv')}
        </div>
      )}

      {/* ── Map canvas + legend (always mounted so Leaflet stays alive; captured for PNG) ── */}
      <div ref={reportAreaRef} className={isReport ? 'pm-report-area' : ''}>
        {/* ── Stats row — shown in both modes; inside reportAreaRef so it appears in PNG ── */}
        {data && (
          <div className="pm-stats">
            {data.totalResponses > 0 && (
              <span>
                <strong>{data.totalResponses}</strong>{' '}
                {t('report.num_responses', { count: data.totalResponses })}
              </span>
            )}
            <span>
              <Trans
                i18nKey="postcode_maps.num_with_postcode_fmt"
                values={{ responsesWithPostcode }}
              />
            </span>
            <span>
              <Trans
                i18nKey="postcode_maps.different_postcodes_fmt"
                values={{
                  num: data.postcodes.length,
                  postcodes: t('postcode_maps.postcodes', {
                    count: data.postcodes.length,
                  }),
                }}
              />
            </span>
            {!isReport && geoStatus === 'loading' && (
              <span className="pm-stats__geo">
                {t('postcode_maps.loading_boundaries')}…
              </span>
            )}
            {!isReport && geoStatus === 'unavailable' && (
              <span className="pm-stats__geo pm-stats__geo--warn">
                ⚠ {t('postcode_maps.boundaries_unavailable')}
              </span>
            )}
          </div>
        )}

        <div ref={mapContainerRef} className="pm-container">
          <div
            style={{
              position: 'relative',
              width: mapW,
              height: mapH,
              background: '#e8ecf0',
              borderRadius: isReport ? 0 : 4,
              overflow: 'hidden',
            }}
          >
            <div ref={mapDivRef} style={{ position: 'absolute', inset: 0 }} />

            {/* Zoom controls — interactive only */}
            {!isReport && (
              <div className="pm-zoom">
                <button
                  type="button"
                  onClick={() => leafletRef.current?.zoomIn()}
                >
                  +
                </button>
                <button
                  type="button"
                  onClick={() => leafletRef.current?.zoomOut()}
                >
                  −
                </button>
              </div>
            )}

            {geoStatus === 'loading' && data && (
              <div className="pm-geo-status">
                {t('postcode_maps.loading_boundaries')}…
              </div>
            )}
            {geoStatus === 'unavailable' && (
              <div className="pm-geo-status pm-geo-status--warn">
                ⚠ {t('postcode_maps.boundaries_unavailable')}
              </div>
            )}
          </div>
        </div>

        {/* ── Selected postcode detail panel ── */}
        {!isReport && selectedPostcode && (
          <div className="pm-detail">
            <div
              className="pm-detail__swatch"
              style={{
                background: solidSwatchColor(selectedPostcode.count, maxCount),
              }}
            />
            <div className="pm-detail__info">
              <span className="pm-detail__code">{selectedCode}</span>
              {selectedLocality && (
                <span className="pm-detail__locality">{selectedLocality}</span>
              )}
            </div>
            <div className="pm-detail__stats">
              <strong>{selectedPostcode.count}</strong>{' '}
              {t('report.responses_plural', { count: selectedPostcode.count })}
              {responsesWithPostcode > 0 && (
                <span className="pm-detail__pct">
                  {' '}
                  · {t('postcode_maps.pct_of_total', { pct: selectedPct })}
                </span>
              )}
            </div>
            <button
              type="button"
              className="pm-detail__close"
              onClick={() => setSelectedCode(null)}
            >
              ×
            </button>
          </div>
        )}

        {/* ── Legend table — boundary mode only ── */}
        {data && data.postcodes.length > 0 && mapType === 'boundary' && (
          <div className="pm-legend">
            {/* Controls row — interactive only */}
            {!isReport && (
              <div className="pm-legend__controls">
                <span className="pm-legend__label">
                  {t('postcode_maps.postcode_origin')}
                </span>
                <button
                  type="button"
                  className="pm-legend__toggle"
                  onClick={() => setShowLegend((v) => !v)}
                >
                  {showLegend
                    ? t('postcode_maps.hide_table')
                    : t('postcode_maps.show_table')}
                </button>
                {showLegend && (
                  <span className="pm-legend__hint">
                    {t('postcode_maps.click_a_row_to_zoom')}
                  </span>
                )}
              </div>
            )}

            {/* Grid — always expanded in report view */}
            {(isReport || showLegend) && (
              <div className="pm-legend__grid">
                {data.postcodes.map((p, i) => {
                  const locality =
                    data.localities?.[p.postcode] || arcgisNames[p.postcode];
                  const isOutRange = outRangeCodes.has(p.postcode);
                  const isSelected = !isReport && selectedCode === p.postcode;
                  return (
                    <div
                      key={p.postcode}
                      className={`pm-legend__row${
                        isSelected ? ' pm-legend__row--selected' : ''
                      }`}
                      style={{
                        background: isSelected
                          ? '#eff6ff'
                          : i % 2 === 0
                            ? '#fafafa'
                            : '#fff',
                        borderLeftColor: isSelected
                          ? FILL_COLOR
                          : 'transparent',
                      }}
                      onClick={
                        !isReport
                          ? (e) => handleListClick(e, p.postcode)
                          : undefined
                      }
                    >
                      <div
                        className="pm-legend__swatch"
                        style={{
                          background: isOutRange
                            ? '#e5e7eb'
                            : solidSwatchColor(p.count, maxCount),
                        }}
                      />
                      <span className="pm-legend__code">{p.postcode}</span>
                      <span
                        className={`pm-legend__locality${
                          isOutRange ? ' pm-legend__locality--out' : ''
                        }`}
                      >
                        {locality || <em>{t('common.not_known')}</em>}
                        {isOutRange && (
                          <span className="pm-legend__out-label">
                            {' '}
                            ({t('postcode_maps.outside_map')})
                          </span>
                        )}
                      </span>
                      <span className="pm-legend__count">{p.count}</span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>
      {/* end reportAreaRef */}
    </div>
  );
};

export default PostcodeMap;
